import { api } from '@/api/client';
// import type {
//   TransactionApiResponse,
// } from '@/pages/merchant/Transactions/types';

// Transaction API endpoints
const TRANSACTION_ENDPOINTS = {
  LIST: '/merchant/v1/transactions/',
  FILTER: '/merchant/v1/transactions/',
  INITIATE_PAYMENT: '/merchant/v1/internal/initiate-payment/',
  // CREATE: '/merchant/v1/transactions/',
  // UPDATE: (id: string) => `/merchant/v1/transactions/${id}/`,
  // DELETE: (id: string) => `/merchant/v1/transactions/${id}/`,
  STATS: '/merchant/v1/transactions/stats/',
  GENERATE_REPORT: '/merchant/v1/generate-report/',
  SCHEDULED_REPORTS: '/merchant/v1/merchant-reports/',
  SCHEDULED_REPORTS_LIST: '/merchant/v1/scheduled-reports/',
  DELETE_SCHEDULED_REPORT: (id: string, reportType: string) => 
    `/merchant/v1/delete-scheduled-report/${id}/?report_type=${reportType}`,
  // EXPORT: '/merchant/v1/transactions/export/',
} as const;

// Query parameters interface for list endpoint
export interface TransactionListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  payment_gateway?: string;
  start_date?: string;
  end_date?: string;
  min_amount?: number;
  max_amount?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// Generate report request interface
export interface GenerateReportRequest {
  report_name: string;
  request_report_type: 'quick' | 'scheduled';
  report_type: string;
  report_format: 'xlsx' | 'csv' | 'zip' | 'pdf';
  date_range_filter?: string;
  column_config: string[];
  report_start_date?: string;
  report_end_date?: string;
}

// Scheduled report request interface
export interface ScheduledReportRequest {
  report_name: string;
  request_report_type: 'scheduled';
  report_type: string;
  report_format: 'xlsx' | 'csv' | 'zip' | 'pdf';
  scheduled_frequency: 'daily' | 'weekly' | 'monthly';
  scheduled_time: string; // HH:MM:SS format
  scheduled_day?: number; // 0-6 for weekly (Monday=0)
  scheduled_date?: number; // 1-31 for monthly
  report_start_date: string;
  report_end_date: string;
  column_config: string[];
}

// Scheduled report response interface
export interface ScheduledReport {
  id: string;
  report_name: string;
  scheduled_frequency: 'daily' | 'weekly' | 'monthly';
  scheduled_time: string;
  scheduled_day?: number;
  scheduled_date?: number;
  report_format: string;
  column_config: string[];
  created_at?: string;
  is_active?: boolean;
}

// Scheduled report item from paginated API
export interface ScheduledReportItem {
  schedule_id: string;
  report_id: number;
  report_name: string;
  report_type: string;
  format: string;
  date_range: {
    start: string;
    end: string;
  };
  frequency: 'daily' | 'weekly' | 'monthly';
  day: number | null;
  date: number | null;
  time: string;
  next_run: string;
  created_at: string;
}

// Paginated scheduled reports response
export interface ScheduledReportsPaginatedResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: ScheduledReportItem[];
}

// Download report interface for the Downloads tab
export interface DownloadReport {
  id: string;
  file_name: string;
  creation_date: string;
  is_active: boolean;
  download_url?: string;
}

// Download reports API response interface
export interface DownloadReportsResponse {
  success: boolean;
  message: string;
  data: DownloadReport[];
}

class TransactionService {
  /**
   * Get paginated list of transactions
   */
  async getTransactions(page: number = 1, pageSize: number = 50): Promise<any> {
    try {
      const url = `${TRANSACTION_ENDPOINTS.LIST}`;
      const response = await api.get<any>(url, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });

      return response;

    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transactions');
    }
  }

  /**
   * Get single transaction by ID
   */
  async getTransactionData(id?: string, merchantTransactionId?: string): Promise<any> {
    try {
      if (!id && !merchantTransactionId) {
        throw new Error('Either transaction_id or merchant_transaction_id must be provided');
      }

      const queryParams = new URLSearchParams();

      if (id) {
        queryParams.append('transaction_id', id);
      }

      if (merchantTransactionId) {
        queryParams.append('merchant_transaction_id', merchantTransactionId);
      }

      const url = `${TRANSACTION_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
 * Get single transaction by ID
 */
  async getTransactionStatusData(status?: any): Promise<any> {
    try {
      if (!status) {
        throw new Error('Either status must be provided');
      }

      const queryParams = new URLSearchParams();

      if (status) {
        queryParams.append('transaction_status', status);
      }

      const url = `${TRANSACTION_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction'); 
    }
  }

  /**
* Get date range
*/
  async getTransactionDateRangeData(startDate?: any, endDate?: any): Promise<any> {
    try {
      if (!startDate && !endDate) {
        throw new Error('Either Start Date and End Date must be provided');
      }

      const queryParams = new URLSearchParams();

      if (startDate) {
        queryParams.append('start_date', startDate);
      }

      if (endDate) {
        queryParams.append('end_date', endDate);
      }

      const url = `${TRANSACTION_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
   * Initiate a payment by transaction amount
   */
  async initiatePayment(transactionAmount: string): Promise<any> {
    try {
      const payload = { transaction_amount: transactionAmount } as const;
      const response = await api.post<any>(TRANSACTION_ENDPOINTS.INITIATE_PAYMENT, payload);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to Simulate payment');
    }
  }

  // /**
  //  * Create new transaction
  //  */
  // async createTransaction(data: CreateTransactionRequest): Promise<Transaction> {
  //   try {
  //     const response = await api.post<{ data: Transaction }>(TRANSACTION_ENDPOINTS.CREATE, data);
  //     return response.data.data;
  //   } catch (error) {
  //     throw this.handleError(error, 'Failed to create transaction');
  //   }
  // }

  // /**
  //  * Update existing transaction
  //  */
  // async updateTransaction(id: string, data: UpdateTransactionRequest): Promise<Transaction> {
  //   try {
  //     const response = await api.put<{ data: Transaction }>(TRANSACTION_ENDPOINTS.UPDATE(id), data);
  //     return response.data.data;
  //   } catch (error) {
  //     throw this.handleError(error, 'Failed to update transaction');
  //   }
  // }

  // /**
  //  * Delete transaction
  //  */                                                                                                nc deleteTransaction(id: string): Promise<void> {
  //   try {
  //     await api.delete(TRANSACTION_ENDPOINTS.DELETE(id));
  //   } catch (error) {
  //     throw this.handleError(error, 'Failed to delete transaction');
  //   }
  // }

  /**
   * Get transaction statistics
   */
  async getTransactionStats(): Promise<any> {
    try {
      const response = await api.get<any>(TRANSACTION_ENDPOINTS.STATS);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction statistics');
    }
  }

  /**
   * Generate and download report
   */
  async generateReport(request: GenerateReportRequest): Promise<Blob> {
    try {
      const response = await api.post<any>(TRANSACTION_ENDPOINTS.GENERATE_REPORT, request, {
        responseType: 'blob',
      });
      // Handle different response structures
      if (response instanceof Blob) {
        return response;
      }
      if (response && response.data instanceof Blob) {
        return response.data;
      }
      if (response && typeof response === 'object' && 'data' in response) {
        return new Blob([response.data]);
      }
      // Fallback: try to create blob from response
      return new Blob([response]);
    } catch (error) {
      throw this.handleError(error, 'Failed to generate report');
    }
  }

  /**
   * Create scheduled report
   */
  async createScheduledReport(request: ScheduledReportRequest): Promise<any> {
    try {
      const response = await api.post<any>(TRANSACTION_ENDPOINTS.GENERATE_REPORT, request);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to create scheduled report');
    }
  }

  /**
   * Get scheduled reports list
   */
  async getScheduledReports(): Promise<ScheduledReport[]> {
    try {
      const response = await api.get<any>(TRANSACTION_ENDPOINTS.SCHEDULED_REPORTS);
      return response?.data || response || [];
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch scheduled reports');
    }
  }

  /**
   * Get scheduled reports list with pagination
   */
  async getScheduledReportsPaginated(
    reportType: string = 'payments',
    page: number = 1,
    pageSize: number = 20
  ): Promise<ScheduledReportsPaginatedResponse> {
    try {
      const url = `${TRANSACTION_ENDPOINTS.SCHEDULED_REPORTS}?report_type=${reportType}&page=${page}&page_size=${pageSize}`;
      const response = await api.get<any>(url);
      return {
        success: response?.success ?? true,
        count: response?.count ?? 0,
        page: response?.page ?? page,
        page_size: response?.page_size ?? pageSize,
        total_pages: response?.total_pages ?? 1,
        results: response?.results || [],
      };
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch scheduled reports');
    }
  }

  /**
   * Delete scheduled report
   */
  async deleteScheduledReport(id: string, reportType: string = 'payments'): Promise<void> {
    try {
      await api.delete(TRANSACTION_ENDPOINTS.DELETE_SCHEDULED_REPORT(id, reportType));
    } catch (error) {
      throw this.handleError(error, 'Failed to delete scheduled report');
    }
  }

  /**
   * Get download reports list for the Downloads tab
   */
  async getDownloadReports(reportType: string = 'payments'): Promise<any> {
    try {
      const response = await api.get<any>(`${TRANSACTION_ENDPOINTS.SCHEDULED_REPORTS}?report_type=${reportType}`);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch download reports');
    }
  }

  /**
   * Get scheduled reports list from the new endpoint
   */
  async getScheduledReportsList(reportType: string = 'payments'): Promise<any> {
    try {
      const response = await api.get<any>(`${TRANSACTION_ENDPOINTS.SCHEDULED_REPORTS_LIST}?report_type=${reportType}`);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch scheduled reports');
    }
  }

  // /**
  //  * Export transactions
  //  */
  // async exportTransactions(params: TransactionListParams = {}, format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
  //   try {
  //     const queryParams = new URLSearchParams();

  //     // Add all params
  //     Object.entries(params).forEach(([key, value]) => {
  //       if (value !== undefined && value !== null) {
  //         queryParams.append(key, value.toString());
  //       }
  //     });

  //     queryParams.append('format', format);

  //     const url = `${TRANSACTION_ENDPOINTS.EXPORT}?${queryParams.toString()}`;
  //     const response = await api.get<Blob>(url, {
  //       responseType: 'blob',
  //     });

  //     return response.data;
  //   } catch (error) {
  //     throw this.handleError(error, 'Failed to export transactions');
  //   }
  // }

  /**
   * Handle API errors
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (error && typeof error === 'object' && 'message' in error) {
      return error as Error;
    }
    return new Error(defaultMessage);
  }
}

// Create singleton instance
export const transactionService = new TransactionService();

// Export for testing
export { TransactionService };

